import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const CONVERSATION_DRAG_TYPE = "application/x-qnneal-conversation";
export const PROJECT_DRAG_TYPE = "application/x-qnneal-project";
const CHANGED = "qnneal:workspace-changed";
export const NEW_CHAT_EVENT = "qnneal:new-chat";

let cache = null;
let inflight = null;
let convCache = null;
let convInflight = null;

export async function fetchProjects(force = false) {
  if (cache && !force) return cache;
  if (force || !inflight) {
    inflight = axios.get(`${API}/workspace/projects`)
      .then(({ data }) => { cache = data.projects || []; return cache; })
      .catch(() => cache || [])
      .finally(() => { inflight = null; });
  }
  return inflight;
}

export async function fetchConversations(force = false) {
  if (convCache && !force) return convCache;
  if (force || !convInflight) {
    convInflight = axios.get(`${API}/workspace/conversations`)
      .then(({ data }) => { convCache = data.conversations || []; return convCache; })
      .catch(() => convCache || [])
      .finally(() => { convInflight = null; });
  }
  return convInflight;
}

export function notifyWorkspaceChanged() {
  cache = null;
  convCache = null;
  window.dispatchEvent(new Event(CHANGED));
}

// Shared, refreshable list of the projects the current user may see.
export function useWorkspaceProjects() {
  const [projects, setProjects] = useState(cache);
  const reload = useCallback(async () => setProjects(await fetchProjects(true)), []);
  useEffect(() => {
    fetchProjects().then(setProjects);
    const h = () => reload();
    window.addEventListener(CHANGED, h);
    return () => window.removeEventListener(CHANGED, h);
  }, [reload]);
  return { projects: projects || [], reload };
}

// Shared, refreshable list of the current user's conversations.
export function useWorkspaceConversations() {
  const [conversations, setConversations] = useState(convCache);
  const reload = useCallback(async () => setConversations(await fetchConversations(true)), []);
  useEffect(() => {
    fetchConversations().then(setConversations);
    const h = () => reload();
    window.addEventListener(CHANGED, h);
    return () => window.removeEventListener(CHANGED, h);
  }, [reload]);
  return { conversations: conversations || [], reload };
}

export async function moveConversation(conversationId, projectId) {
  const { data } = await axios.post(`${API}/workspace/conversations/${conversationId}/move`, { project_id: projectId || null });
  notifyWorkspaceChanged();
  return data;
}

export async function renameConversation(conversationId, title) {
  const { data } = await axios.patch(`${API}/workspace/conversations/${conversationId}`, { title });
  notifyWorkspaceChanged();
  return data;
}

export async function pinConversation(conversationId, pinned) {
  const { data } = await axios.patch(`${API}/workspace/conversations/${conversationId}`, { pinned });
  notifyWorkspaceChanged();
  return data;
}

export async function archiveConversation(conversationId, archived) {
  const { data } = await axios.patch(`${API}/workspace/conversations/${conversationId}`, { archived });
  notifyWorkspaceChanged();
  return data;
}

export async function deleteConversation(conversationId) {
  const { data } = await axios.delete(`${API}/workspace/conversations/${conversationId}`);
  notifyWorkspaceChanged();
  return data;
}

export async function reorderPins(conversationIds) {
  const { data } = await axios.post(`${API}/workspace/conversations/pins/reorder`, { conversation_ids: conversationIds });
  notifyWorkspaceChanged();
  return data;
}

export async function renameProject(projectId, name) {
  const { data } = await axios.patch(`${API}/workspace/projects/${projectId}`, { name });
  notifyWorkspaceChanged();
  return data;
}

export async function pinProject(projectId, pinned) {
  const { data } = await axios.patch(`${API}/workspace/projects/${projectId}`, { pinned });
  notifyWorkspaceChanged();
  return data;
}

export async function archiveProject(projectId, archived) {
  const { data } = await axios.patch(`${API}/workspace/projects/${projectId}`, { archived });
  notifyWorkspaceChanged();
  return data;
}

export async function deleteProject(projectId) {
  const { data } = await axios.delete(`${API}/workspace/projects/${projectId}`);
  notifyWorkspaceChanged();
  return data;
}

export async function reorderProjectPins(projectIds) {
  const { data } = await axios.post(`${API}/workspace/projects/pins/reorder`, { project_ids: projectIds });
  notifyWorkspaceChanged();
  return data;
}

// On-demand archived items (chats + projects) for the "Archived" toggle in the rail.
export function useArchived(enabled) {
  const [data, setData] = useState({ chats: [], projects: [] });
  const reload = useCallback(async () => {
    if (!enabled) return;
    try {
      const [c, p] = await Promise.all([
        axios.get(`${API}/workspace/conversations`, { params: { include_archived: true } }),
        axios.get(`${API}/workspace/projects`, { params: { include_archived: true } }),
      ]);
      setData({
        chats: (c.data.conversations || []).filter((x) => x.archived),
        projects: (p.data.projects || []).filter((x) => x.archived),
      });
    } catch { setData({ chats: [], projects: [] }); }
  }, [enabled]);
  useEffect(() => {
    reload();
    const h = () => reload();
    window.addEventListener(CHANGED, h);
    return () => window.removeEventListener(CHANGED, h);
  }, [reload]);
  return { ...data, reload };
}

export function requestNewChat() {
  window.dispatchEvent(new Event(NEW_CHAT_EVENT));
}

export function onConversationDrop(projectId, onDone) {
  return {
    onDragOver: (e) => { if ([...e.dataTransfer.types].includes(CONVERSATION_DRAG_TYPE)) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; } },
    onDrop: async (e) => {
      const id = e.dataTransfer.getData(CONVERSATION_DRAG_TYPE);
      if (!id) return;
      e.preventDefault();
      try { await moveConversation(id, projectId); onDone?.(true); } catch { onDone?.(false); }
    },
  };
}

export function useWorkspaceChanged(handler) {
  useEffect(() => {
    window.addEventListener(CHANGED, handler);
    return () => window.removeEventListener(CHANGED, handler);
  }, [handler]);
}
